'use client';

import { createPortal } from 'react-dom';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';
import { Calculator, CalendarDays, CheckCircle2, Clock3, Crown, FlaskConical, Globe2, Languages, Sigma, Sparkles, X } from 'lucide-react';
import useAuthStore from '@/store/useAuthStore';
import { getCurrentTier } from '@/store/useSubscriptionStore';
import { apiClient } from '@/lib/api-client';
import NormalButton from '@/components/Button/normal';
import MembershipModal from '@/components/MemberShip/MembershipModal';
import LinkParentAccountModal from '@/components/LinkParentAccountModal';
import SwitchToParentAccountModal from '@/components/SwitchToParentAccountModal';

const SUBJECT_OPTIONS = [
  { value: 'math', label: 'Math', icon: Calculator },
  { value: 'science', label: 'Science', icon: FlaskConical },
  { value: 'history', label: 'Social Studies', icon: Globe2 },
  { value: 'vocabulary', label: 'Vocabulary', icon: Languages },
  { value: 'grammar', label: 'Grammar', icon: BookIcon },
  { value: 'amc-math', label: 'AMC Math', icon: Sigma },
];

function BookIcon(props: ComponentProps<typeof Languages>) {
  return <Languages {...props} />;
}

const SUBJECT_LABELS = SUBJECT_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const SUBJECT_COOLDOWN_DAYS = 7;

function formatDisplayDate(value: Date | null) {
  if (!value) return 'Not selected yet';
  return value.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface SubjectManagementModalProps {
  visible: boolean;
  onClose: () => void;
  preferredSubject?: string | null;
}

function normalizeSubject(value: string | null | undefined) {
  return String(value || '').trim().toLowerCase().replace(/_/g, '-');
}

export default function SubjectManagementModal({ visible, onClose, preferredSubject = null }: SubjectManagementModalProps) {
  const profile = useAuthStore((state) => state.profile);
  const tier = getCurrentTier();
  const [selectedSubject, setSelectedSubject] = useState(normalizeSubject(profile?.free_subject));
  const [pendingConfirmationSubject, setPendingConfirmationSubject] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showLinkParentModal, setShowLinkParentModal] = useState(false);
  const [showSwitchParentModal, setShowSwitchParentModal] = useState(false);

  const lockedSubject = normalizeSubject(profile?.free_subject) || null;
  const changedAtRaw = profile?.subject_changed_at ?? null;
  const selectedAt = changedAtRaw ? new Date(changedAtRaw) : null;

  const cooldownState = useMemo(() => {
    const changedAt = changedAtRaw ? new Date(changedAtRaw) : null;
    const now = new Date();
    const daysSinceChange = changedAt ? (now.getTime() - changedAt.getTime()) / (1000 * 60 * 60 * 24) : Infinity;
    const onCooldown = daysSinceChange < SUBJECT_COOLDOWN_DAYS;
    const nextAvailable = changedAt
      ? new Date(changedAt.getTime() + SUBJECT_COOLDOWN_DAYS * 24 * 60 * 60 * 1000)
      : null;

    return { onCooldown, nextAvailable };
  }, [changedAtRaw]);

  useEffect(() => {
    if (!visible) return;
    const nextSelectedSubject = normalizeSubject(preferredSubject || profile?.free_subject);
    setSelectedSubject(nextSelectedSubject);
    const nextIsPaid = tier !== 'free';
    if (!nextIsPaid && !cooldownState.onCooldown && nextSelectedSubject && nextSelectedSubject !== normalizeSubject(profile?.free_subject)) {
      setPendingConfirmationSubject(nextSelectedSubject);
    } else {
      setPendingConfirmationSubject(null);
    }
    setError('');
    setShowMembershipModal(false);
    setShowLinkParentModal(false);
    setShowSwitchParentModal(false);
  }, [cooldownState.onCooldown, preferredSubject, profile?.free_subject, tier, visible]);

  if (typeof window === 'undefined') {
    return null;
  }

  const subjectLabel = lockedSubject ? (SUBJECT_LABELS[lockedSubject] ?? lockedSubject) : 'Not set';
  const isPaid = tier !== 'free';
  const canChangeNow = !isPaid && !cooldownState.onCooldown;
  const hasCurrentSubject = Boolean(lockedSubject);
  const hasPendingSelection = Boolean(selectedSubject && normalizeSubject(selectedSubject) !== normalizeSubject(lockedSubject));
  const shouldShowSelectionConfirmation = Boolean(!isPaid && canChangeNow && pendingConfirmationSubject && normalizeSubject(pendingConfirmationSubject) === normalizeSubject(selectedSubject));
  const nextChangeLabel = isPaid
    ? 'Any time with Plus'
    : cooldownState.nextAvailable
      ? formatDisplayDate(cooldownState.nextAvailable)
      : 'After your first confirmation';
  const selectedAtLabel = lockedSubject ? formatDisplayDate(selectedAt) : 'Not selected yet';

  const handleUpgrade = () => {
    onClose();
    setTimeout(() => setShowMembershipModal(true), 100);
  };

  const handleSave = async (subjectOverride?: string) => {
    const nextSubject = normalizeSubject(subjectOverride || selectedSubject);
    if (!nextSubject || (!isPaid && !canChangeNow) || nextSubject === lockedSubject) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const res = await apiClient.patch('/api/profile/subject', { subject: nextSubject });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setError(data?.error || 'Failed to update subject');
        return;
      }

      useAuthStore.setState((state) => ({
        profile: state.profile
          ? {
              ...state.profile,
              free_subject: nextSubject,
              subject_changed_at: new Date().toISOString(),
            }
          : null,
      }));

      setPendingConfirmationSubject(null);
      onClose();
    } catch {
      setError('Failed to update subject');
    } finally {
      setSaving(false);
    }
  };

  const handleSubjectSelect = (subjectValue: string) => {
    const nextSubject = normalizeSubject(subjectValue);
    setSelectedSubject(nextSubject);
    setError('');

    if (isPaid) {
      setPendingConfirmationSubject(null);
      return;
    }

    if (!canChangeNow || nextSubject === lockedSubject) {
      setPendingConfirmationSubject(null);
      return;
    }

    setPendingConfirmationSubject(nextSubject);
  };

  const handleCancelPendingConfirmation = () => {
    setPendingConfirmationSubject(null);
    setSelectedSubject(lockedSubject ?? '');
  };

  const confirmationSubjectLabel = pendingConfirmationSubject
    ? (SUBJECT_LABELS[pendingConfirmationSubject] ?? pendingConfirmationSubject)
    : '';
  const modalTitle = hasCurrentSubject ? 'Change Subject' : 'Choose Subject';
  const statusSummary = !hasCurrentSubject
    ? 'No subject selected yet'
    : canChangeNow
      ? `Current subject: ${subjectLabel} · Change available`
      : `Current subject: ${subjectLabel}`;

  return (
    <>
      {visible && createPortal(
        <div
          className="fixed inset-0 flex items-center justify-center p-3 sm:p-4"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1300 }}
          onClick={onClose}
        >
          <div
            className="relative w-full max-w-[880px] max-h-[92vh] overflow-hidden rounded-[22px] bg-white px-4 pb-4 pt-4 shadow-[0_20px_56px_rgba(15,23,42,0.18)] animate-fade-in sm:px-5 sm:pb-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-16 bg-[linear-gradient(135deg,rgba(255,220,188,0.55)_0%,rgba(243,244,245,0.2)_70%)]" />
            <div className="relative max-h-[92vh] overflow-y-auto pb-1">
              <div className="relative flex items-start justify-center gap-4 pt-[8px]">
                <div className="px-10">
                  <h3 className="display-text-bold whitespace-nowrap text-center text-[1.28rem] tracking-tight text-[#111111] sm:text-[1.45rem]">{modalTitle}</h3>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-0 top-[2px] flex h-9 w-9 items-center justify-center rounded-full text-[#667085] transition-colors hover:bg-[#F3F4F6] hover:text-[#111827]"
                  aria-label="Close subject dialog"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-[24px] grid gap-4 sm:mt-[28px] lg:grid-cols-[0.95fr_1.05fr]">
                <div>
                  <div className="hidden items-center justify-center gap-3 sm:flex">
                    <p className="display-text-bold text-center text-[0.95rem] text-[#1E1E20] sm:text-[1rem]">Current status</p>
                  </div>

                  <div className="mt-0 rounded-[18px] bg-[#F3F5F8] p-3.5 sm:mt-3 sm:p-4">
                    <div className="mb-3 rounded-[14px] bg-white/80 px-3 py-2">
                      <p className="display-text-semi text-[12px] text-[#4B5563]">{statusSummary}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                          <Crown className="h-4 w-4 text-[#6541C4]" />
                        </div>
                        <div>
                          <p className="display-text text-[9px] font-bold uppercase tracking-[0.12em] text-[#6B7280]">Current status</p>
                          <p className="display-text-semi text-[12px] text-[#1E1E20]">{isPaid ? 'Plus plan' : 'Free plan'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                          <Sparkles className="h-4 w-4 text-[#FBAF2C]" />
                        </div>
                        <div>
                          <p className="display-text text-[9px] font-bold uppercase tracking-[0.12em] text-[#6B7280]">Selected subject</p>
                          <p className="display-text-semi text-[12px] text-[#1E1E20]">{subjectLabel}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white">
                          <CalendarDays className="h-4 w-4 text-[#6B7280]" />
                        </div>
                        <div>
                          <p className="display-text text-[9px] font-bold uppercase tracking-[0.12em] text-[#6B7280]">Selected on</p>
                          <p className="display-text-semi text-[12px] text-[#1E1E20]">{selectedAtLabel}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FEB700]/20">
                          <Clock3 className="h-4 w-4 text-[#785500]" />
                        </div>
                        <div>
                          <p className="display-text text-[9px] font-bold uppercase tracking-[0.12em] text-[#6B7280]">Can change again</p>
                          <p className="display-text-semi text-[12px] text-[#785500]">{nextChangeLabel}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {!isPaid && (
                    <div className="mt-3 flex gap-2.5 rounded-[14px] bg-[#EEE9FF]/45 px-3.5 py-2.5">
                      <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[#6541C4]" />
                      <p className="display-text text-[11px] leading-5 text-[#6B7280] sm:text-[12px]">
                        {hasCurrentSubject
                          ? <>Choose the subject you want to focus on next. Free users can switch again after {SUBJECT_COOLDOWN_DAYS} days.</>
                          : <>Choose your learning subject. Pick the one you want to focus on first.</>}
                      </p>
                    </div>
                  )}
                  {isPaid && (
                    <div className="mt-3 flex gap-2.5 rounded-[14px] bg-[#EEE9FF]/45 px-3.5 py-2.5">
                      <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-[#6541C4]" />
                      <p className="display-text text-[12px] leading-5 text-[#6B7280]">
                        Plus unlocks every subject, so you are not limited to a single free-plan choice.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  {!isPaid && (
                    <div className="mt-1 lg:mt-0">
                      <div className="flex items-center justify-center gap-3">
                        <p className="display-text-bold text-center text-[0.95rem] text-[#1E1E20] sm:text-[1rem]">Choose subject</p>
                      </div>
                      <p className="display-text mt-1 flex items-center justify-center gap-1 text-[11px] text-[#6B7280] sm:text-[12px]">
                        <button
                          type="button"
                          onClick={handleUpgrade}
                          className="display-text-bold inline-flex items-center gap-1 text-[#E59200] underline underline-offset-2"
                        >
                          <Sparkles className="h-3 w-3" />
                          Upgrade to Plus
                        </button>{' '}
                        to unlock all subjects.
                      </p>
                      <div className="mt-3 grid grid-cols-3 gap-2.5 sm:gap-3">
                        {SUBJECT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleSubjectSelect(option.value)}
                            className={`group relative flex min-h-[80px] flex-col items-center justify-center gap-1.5 rounded-[16px] border-2 p-2 text-sm transition-all sm:min-h-[88px] sm:gap-2 sm:p-2.5 ${
                              selectedSubject === option.value
                                ? 'border-[#FEB700] bg-[#FFF6E3] text-[#1E1E20] shadow-[0_12px_24px_rgba(251,175,44,0.16)]'
                                : 'border-[#D8DEE8] bg-[#FBFCFD] text-[#4B5563] hover:border-[#FBAF2C] hover:bg-[#FFF9EE]'
                            }`}
                          >
                            {selectedSubject === option.value ? (
                              <CheckCircle2 className="absolute right-2 top-2 h-3.5 w-3.5 text-[#FBAF2C]" />
                            ) : null}
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white ring-1 ring-black/5 sm:h-9 sm:w-9">
                              <option.icon className="h-4 w-4 text-[#FBAF2C] sm:h-4.5 sm:w-4.5" />
                            </div>
                            <span className="display-text-semi text-center text-[11px] leading-tight sm:text-[12px]">{option.label}</span>
                          </button>
                        ))}
                      </div>
                      {error && <p className="display-text mt-3 text-[12px] text-red-500">{error}</p>}
                    </div>
                  )}

                  {isPaid && (
                    <div className="mt-1 rounded-[18px] bg-[#FFF7E8] px-4 py-3 lg:mt-0">
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-[#FBAF2C]" />
                        <p className="display-text-semi text-sm text-[#7A560E]">Plus unlocks all subjects</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {shouldShowSelectionConfirmation && (
                <div className="mx-auto mt-4 w-full max-w-[760px] rounded-[18px] border border-[#F3D79D] bg-[#FFF8E8] px-4 py-3 text-center">
                  <p className="display-text-bold text-[14px] text-[#7A560E]">
                    {hasCurrentSubject
                      ? `Switch your subject to ${confirmationSubjectLabel} for the next 7 days?`
                      : `Make ${confirmationSubjectLabel} your subject for the next 7 days?`}
                  </p>
                  <p className="display-text mt-1 text-[12px] leading-5 text-[#7A560E]/80">
                    {hasCurrentSubject
                      ? `This will replace ${subjectLabel} as your current subject.`
                      : 'Free users learn one subject at a time. You can switch again in 7 days.'}
                  </p>
                </div>
              )}

              {!isPaid && !canChangeNow && cooldownState.nextAvailable && (
                <p className="display-text mt-4 text-center text-[12px] text-[#6B7280]">
                  You can confirm a new subject after {formatDisplayDate(cooldownState.nextAvailable)}.
                </p>
              )}

              
              <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
                {!isPaid && (
                  <>
                    {shouldShowSelectionConfirmation ? (
                      <>
                        <NormalButton
                          onClick={handleCancelPendingConfirmation}
                          className="h-10 w-full flex-1 rounded-full"
                          colorType="white"
                          disabled={saving}
                        >
                          <span className="text-[12px]">Cancel</span>
                        </NormalButton>
                        <NormalButton
                          onClick={() => void handleSave(pendingConfirmationSubject || undefined)}
                          className="h-10 flex-1 rounded-full"
                          colorType="green"
                          disabled={saving}
                        >
                          <span className="text-[12px]">{saving ? 'Saving...' : 'Confirm'}</span>
                        </NormalButton>
                      </>
                    ) : (
                      <>
                        <NormalButton
                          onClick={handleUpgrade}
                          className="h-10 w-full flex-1 rounded-full"
                          colorType="green"
                        >
                          <span className="text-[12px]">Upgrade to Plus</span>
                        </NormalButton>
                        <NormalButton
                          onClick={onClose}
                          className="h-10 flex-1 rounded-full"
                          colorType="white"
                        >
                          <span className="text-[12px]">{hasPendingSelection && canChangeNow ? 'Review selection' : 'Close'}</span>
                        </NormalButton>
                      </>
                    )}
                  </>
                )}
                {isPaid && (
                  <>
                    <NormalButton
                      onClick={() => void handleSave()}
                      className="w-full rounded-full"
                      disabled={!selectedSubject || saving || selectedSubject === lockedSubject}
                    >
                      <span className="text-[13px] sm:text-sm">{saving ? 'Saving...' : 'Save subject'}</span>
                    </NormalButton>
                    <NormalButton onClick={onClose} colorType="white" className="w-full rounded-full">
                      <span className="text-[13px] sm:text-sm">Close</span>
                    </NormalButton>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      <MembershipModal
        isOpen={showMembershipModal}
        onClose={() => setShowMembershipModal(false)}
        onOpenLinkParent={() => {
          setShowMembershipModal(false);
          setShowLinkParentModal(true);
        }}
        onOpenSwitchParent={() => {
          setShowMembershipModal(false);
          setShowSwitchParentModal(true);
        }}
        onOpenSubjectManager={() => {
          setShowMembershipModal(false);
        }}
        tier={tier}
      />
      <LinkParentAccountModal
        visible={showLinkParentModal}
        onClose={() => setShowLinkParentModal(false)}
      />
      <SwitchToParentAccountModal
        visible={showSwitchParentModal}
        onClose={() => setShowSwitchParentModal(false)}
      />
    </>
  );
}
